import { KickoffRecord, PagePathsMap } from '../types';

export class AuthError extends Error {
  constructor(message = 'Your session has expired. Redirecting to sign-in.') {
    super(message);
    this.name = 'AuthError';
  }
}

export function redirectToLogin(params?: string): void {
  const query = params ? `?${params}` : '';
  window.location.replace(`login.html${query}`);
}

export async function authenticatedFetch(
  resource: string,
  options: RequestInit = {}
): Promise<Response> {
  const response = await fetch(resource, {
    ...options,
    credentials: 'same-origin',
  });

  if (response.status === 401) {
    redirectToLogin('session_error=1');
    throw new AuthError();
  }

  return response;
}

export async function checkSession(): Promise<{ authenticated: boolean; data?: any }> {
  try {
    const response = await fetch('/auth/session', {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      return { authenticated: true, data };
    }
    return { authenticated: false };
  } catch (error) {
    console.error('Session check failed:', error);
    return { authenticated: false };
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch('/auth/logout', {
      method: 'POST',
      credentials: 'same-origin',
    });
  } catch (err) {
    console.warn('Logout error', err);
  } finally {
    redirectToLogin();
  }
}

export async function fetchHistory(): Promise<KickoffRecord[]> {
  const response = await authenticatedFetch('/api/kickoffs');
  if (!response.ok) {
    throw new Error(`Failed to fetch history: ${response.statusText}`);
  }
  return response.json();
}

export async function fetchPdfBlob(docId: string): Promise<Blob> {
  const response = await authenticatedFetch(`/api/kickoffs/${docId}/pdf`);
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF from backend: ${response.statusText}`);
  }
  return response.blob();
}

export async function updateKickoffPaths(
  kickoffId: string,
  pagePathsMap: PagePathsMap,
  pixelsPerFoot: number | null
): Promise<void> {
  const response = await authenticatedFetch(`/api/kickoffs/${kickoffId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      pagePathsMap,
      pixelsPerFoot,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update paths: ${response.statusText}`);
  }
}

export async function uploadAndProcessPdf(file: File): Promise<KickoffRecord> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await authenticatedFetch('/api/process', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    let serverMessage = `Server responded with status ${response.status}`;
    try {
      const errorData = await response.json();
      serverMessage = errorData.detail || errorData.error || serverMessage;
    } catch {
      // Fallback
    }
    throw new Error(serverMessage);
  }

  return response.json();
}
